import { c as _c } from "react/compiler-runtime";
import { IcoSettings16, IcoShuffle16, IcoTrash16 } from '@onefootprint/icons';
import { Divider, LinkButton, type TemplateInputProps, useConfirmationDialog } from '@onefootprint/ui';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import SettingsSection from '@/components/playbook-builder/components/shared/settings-section';
import useFocusOnQuestion from '../hooks/use-focus-on-question';
import type { CustomQuestionsFormData } from '../types';
import { AddressForm } from './questions/address';
import { CheckboxForm } from './questions/checkbox';
import { CheckboxesForm } from './questions/checkboxes';
import { DropdownForm } from './questions/dropdown';
import { LongTextForm } from './questions/long-text';
import { MultipleChoiceForm } from './questions/multiple-choice';
import { NumberForm } from './questions/number';
import { ShortTextForm } from './questions/short-text';
import { TextBlockForm } from './questions/text-block';
import SettingsLogic, { type SettingsLogicRef } from './settings-logic';
type QuestionSettingsProps = {
  questionIndex: number;
  onDelete: () => void;
  autocompleteItems?: TemplateInputProps['autocompleteItems'];
  canEdit: boolean;
};
export type QuestionSettingsRef = {
  validateBeforeClose: () => Promise<boolean>;
};
const QuestionSettings = forwardRef<QuestionSettingsRef, QuestionSettingsProps>((t0, ref) => {
  const $ = _c(86);
  const {
    questionIndex,
    onDelete,
    autocompleteItems,
    canEdit
  } = t0;
  const {
    t
  } = useTranslation("playbook-custom-questions");
  const confirm = useConfirmationDialog();
  const logicRef = useRef(null);
  const {
    control,
    formState: t1
  } = useFormContext();
  const {
    dirtyFields
  } = t1;
  const t2 = `questions.${questionIndex}`;
  let t3;
  if ($[0] !== control || $[1] !== t2) {
    t3 = {
      control,
      name: t2
    };
    $[0] = control;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const question = useWatch(t3);
  let t4;
  if ($[3] !== control) {
    t4 = {
      control,
      name: "questions"
    };
    $[3] = control;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const questions = useWatch(t4);
  let t5;
  bb0: {
    if (!questions) {
      let t6;
      if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = [];
        $[5] = t6;
      } else {
        t6 = $[5];
      }
      t5 = t6;
      break bb0;
    }
    let t6;
    if ($[6] !== questionIndex || $[7] !== questions) {
      t6 = questions.slice(0, questionIndex).filter(_temp);
      $[6] = questionIndex;
      $[7] = questions;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    t5 = t6;
  }
  const availableQuestions = t5;
  const hasLogic = !!question?.condition;
  useFocusOnQuestion(questionIndex);
  let t6;
  if ($[9] !== confirm || $[10] !== t) {
    t6 = async () => {
      if (logicRef.current?.hasIncompleteRules()) {
        return new Promise(resolve => {
          confirm.open({
            title: t(_temp2),
            description: t(_temp3),
            variant: "destructive",
            primaryButton: {
              label: t(_temp4),
              onClick: () => {
                logicRef.current?.clearLogic();
                resolve(true);
              }
            },
            secondaryButton: {
              label: t(_temp5),
              onClick: () => {
                resolve(false);
              }
            }
          });
        });
      }
      return true;
    };
    $[9] = confirm;
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const validateBeforeClose = t6;
  let t7;
  if ($[12] !== validateBeforeClose) {
    t7 = () => ({
      validateBeforeClose
    });
    $[12] = validateBeforeClose;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  useImperativeHandle(ref, t7);
  if (!question) {
    return null;
  }
  let t8;
  if ($[14] !== hasLogic) {
    t8 = async open => {
      if (open && !hasLogic) {
        logicRef.current?.enableLogic();
        return true;
      }
      if (logicRef.current?.hasIncompleteRules()) {
        logicRef.current?.clearLogic();
      }
      return true;
    };
    $[14] = hasLogic;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const handleLogicOpenChange = t8;
  let t9;
  if ($[16] !== confirm || $[17] !== dirtyFields.questions || $[18] !== onDelete || $[19] !== questionIndex || $[20] !== t) {
    t9 = () => {
      const isQuestionTouched = dirtyFields.questions?.[questionIndex];
      if (!isQuestionTouched) {
        return onDelete();
      }
      confirm.open({
        title: t(_temp6),
        description: t(_temp7),
        variant: "destructive",
        primaryButton: {
          label: t(_temp8),
          onClick: onDelete
        },
        secondaryButton: {
          label: t(_temp9)
        }
      });
    };
    $[16] = confirm;
    $[17] = dirtyFields.questions;
    $[18] = onDelete;
    $[19] = questionIndex;
    $[20] = t;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const handleDelete = t9;
  let t10;
  if ($[22] !== t) {
    t10 = t(_temp10);
    $[22] = t;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== autocompleteItems || $[25] !== question.kind || $[26] !== questionIndex) {
    t11 = question.kind === "text_block" && <TextBlockForm questionIndex={questionIndex} autocompleteItems={autocompleteItems} />;
    $[24] = autocompleteItems;
    $[25] = question.kind;
    $[26] = questionIndex;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== autocompleteItems || $[29] !== question.kind || $[30] !== questionIndex) {
    t12 = question.kind === "short_text" && <ShortTextForm questionIndex={questionIndex} autocompleteItems={autocompleteItems} />;
    $[28] = autocompleteItems;
    $[29] = question.kind;
    $[30] = questionIndex;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== autocompleteItems || $[33] !== question.kind || $[34] !== questionIndex) {
    t13 = question.kind === "long_text" && <LongTextForm questionIndex={questionIndex} autocompleteItems={autocompleteItems} />;
    $[32] = autocompleteItems;
    $[33] = question.kind;
    $[34] = questionIndex;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== autocompleteItems || $[37] !== question.kind || $[38] !== questionIndex) {
    t14 = question.kind === "number" && <NumberForm questionIndex={questionIndex} autocompleteItems={autocompleteItems} />;
    $[36] = autocompleteItems;
    $[37] = question.kind;
    $[38] = questionIndex;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== autocompleteItems || $[41] !== question.kind || $[42] !== questionIndex) {
    t15 = question.kind === "dropdown" && <DropdownForm questionIndex={questionIndex} autocompleteItems={autocompleteItems} />;
    $[40] = autocompleteItems;
    $[41] = question.kind;
    $[42] = questionIndex;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== autocompleteItems || $[45] !== question.kind || $[46] !== questionIndex) {
    t16 = question.kind === "checkboxes" && <CheckboxesForm questionIndex={questionIndex} autocompleteItems={autocompleteItems} />;
    $[44] = autocompleteItems;
    $[45] = question.kind;
    $[46] = questionIndex;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] !== autocompleteItems || $[49] !== question.kind || $[50] !== questionIndex) {
    t17 = question.kind === "checkbox" && <CheckboxForm questionIndex={questionIndex} autocompleteItems={autocompleteItems} />;
    $[48] = autocompleteItems;
    $[49] = question.kind;
    $[50] = questionIndex;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== autocompleteItems || $[53] !== question.kind || $[54] !== questionIndex) {
    t18 = question.kind === "multiple_choice" && <MultipleChoiceForm questionIndex={questionIndex} autocompleteItems={autocompleteItems} />;
    $[52] = autocompleteItems;
    $[53] = question.kind;
    $[54] = questionIndex;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  let t19;
  if ($[56] !== autocompleteItems || $[57] !== question.kind || $[58] !== questionIndex) {
    t19 = question.kind === "address" && <AddressForm questionIndex={questionIndex} autocompleteItems={autocompleteItems} />;
    $[56] = autocompleteItems;
    $[57] = question.kind;
    $[58] = questionIndex;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  let t20;
  if ($[60] !== t10 || $[61] !== t11 || $[62] !== t12 || $[63] !== t13 || $[64] !== t14 || $[65] !== t15 || $[66] !== t16 || $[67] !== t17 || $[68] !== t18 || $[69] !== t19) {
    t20 = <SettingsSection title={t10} icon={IcoSettings16} defaultExpanded={true}>{t11}{t12}{t13}{t14}{t15}{t16}{t17}{t18}{t19}</SettingsSection>;
    $[60] = t10;
    $[61] = t11;
    $[62] = t12;
    $[63] = t13;
    $[64] = t14;
    $[65] = t15;
    $[66] = t16;
    $[67] = t17;
    $[68] = t18;
    $[69] = t19;
    $[70] = t20;
  } else {
    t20 = $[70];
  }
  let t21;
  if ($[71] !== availableQuestions || $[72] !== canEdit || $[73] !== handleLogicOpenChange || $[74] !== hasLogic || $[75] !== questionIndex || $[76] !== t) {
    t21 = availableQuestions.length > 0 && <><Divider /><SettingsSection title={t(_temp11)} icon={IcoShuffle16} defaultExpanded={hasLogic} onOpenChange={canEdit ? handleLogicOpenChange : undefined}><SettingsLogic ref={logicRef} questionIndex={questionIndex} availableQuestions={availableQuestions} canEdit={canEdit} /></SettingsSection></>;
    $[71] = availableQuestions;
    $[72] = canEdit;
    $[73] = handleLogicOpenChange;
    $[74] = hasLogic;
    $[75] = questionIndex;
    $[76] = t;
    $[77] = t21;
  } else {
    t21 = $[77];
  }
  let t22;
  if ($[78] !== canEdit || $[79] !== handleDelete || $[80] !== t) {
    t22 = canEdit && <><Divider /><div className="p-3 pt-2"><LinkButton destructive={true} onClick={handleDelete} iconComponent={IcoTrash16} iconPosition="left">{t(_temp12)}</LinkButton></div></>;
    $[78] = canEdit;
    $[79] = handleDelete;
    $[80] = t;
    $[81] = t22;
  } else {
    t22 = $[81];
  }
  let t23;
  if ($[82] !== t20 || $[83] !== t21 || $[84] !== t22) {
    t23 = <div className="bg-primary border border-tertiary rounded"><div className="flex flex-col">{t20}{t21}{t22}</div></div>;
    $[82] = t20;
    $[83] = t21;
    $[84] = t22;
    $[85] = t23;
  } else {
    t23 = $[85];
  }
  return t23;
});
QuestionSettings.displayName = 'QuestionSettings';
export default QuestionSettings;
function _temp(q) {
  return !!q && q.kind !== "text_block";
}
function _temp2($) {
  return $.confirmLogic.title;
}
function _temp3($_0) {
  return $_0.confirmLogic.description;
}
function _temp4($_1) {
  return $_1.confirmLogic.removeLogic;
}
function _temp5($_2) {
  return $_2.confirmLogic.keepEditing;
}
function _temp6($_3) {
  return $_3.confirmDelete.title;
}
function _temp7($_4) {
  return $_4.confirmDelete.description;
}
function _temp8($_5) {
  return $_5.confirmDelete.delete;
}
function _temp9($_6) {
  return $_6.confirmDelete.cancel;
}
function _temp10($_7) {
  return $_7.questionSettings.basicSettings;
}
function _temp11($_8) {
  return $_8.questionSettings.logic;
}
function _temp12($_9) {
  return $_9.questionSettings.deleteQuestion;
}